import React, { useState, useEffect, useRef } from 'react';

interface SearchInputProps {
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  initialValue?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  placeholder = 'Search...',
  debounceMs = 300,
  className = '',
  initialValue = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);

  // Keep internal state in sync if parent updates initialValue
  useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  // Debounced search function using ref to avoid unknown deps warning
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onSearch(searchTerm);
    }, debounceMs);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs, onSearch]);

  const handleClear = () => {
    setSearchTerm('');
  };

  return (
    <div className={`search-input-container ${className}`}>
      <div className="search-input-wrapper">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
          autoComplete="off"
          placeholder={placeholder}
          className="search-input"
        />
        <div className="search-input-icons">
          {searchTerm && (
            <button
              type="button"
              onClick={handleClear}
              className="search-clear-btn"
              title="Clear search"
            >
              ✕
            </button>
          )}
          <span className="search-icon">🔍</span>
        </div>
      </div>
    </div>
  );
};

// Debounce utility function
export default SearchInput;